import React from 'react';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import { AccessTime, AutoAwesome, LockOpen, WorkspacePremium } from '@mui/icons-material';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import CitySearch from '../components/forms/CitySearch';
import PremiumTrialPopup from '../components/PremiumTrialPopup';
import { usePremiumFeatureAccess } from '../hooks/usePremiumFeatureAccess';
import DailyAstrologyShareCard from '../components/DailyAstrologyShareCard';

const API_BASE_URL = process.env.REACT_APP_STUDIO_API_URL || 'http://localhost:4173/api/studio/v1';

interface City {
  id: string;
  name: string;
  displayName: string;
  coordinates: { latitude: number; longitude: number };
  timezone: { name: string; offset: number };
  population: number;
  admin1?: { name: string; code?: string };
  country?: { name: string; code: string };
}

interface MuhuratSlot {
  type: string;
  activity: string;
  activityLabel?: string;
  start: string;
  end: string;
  durationMin: number;
  score: number;
  choghadiya: string;
  choghadiyaLabel?: string;
  tithi?: string | null;
  tithiLabel?: string | null;
  nakshatra?: string | null;
  nakshatraLabel?: string | null;
  moonRashi?: string | null;
  moonRashiLabel?: string | null;
  hora?: string | null;
  horaLabel?: string | null;
  reasons: string[];
  warnings: string[];
  tara?: { taraName: string; taraLabel?: string; isGood: boolean } | null;
  chandraBala?: { position: number; isGood: boolean } | null;
  explanation?: any;
}

const ACTIVITIES = [
  { key: 'general', en: 'General', hi: 'सामान्य' },
  { key: 'business', en: 'Business', hi: 'व्यापार' },
  { key: 'travel', en: 'Travel', hi: 'यात्रा' },
  { key: 'purchase', en: 'Purchase', hi: 'खरीदारी' },
  { key: 'marriage', en: 'Marriage', hi: 'विवाह' },
  { key: 'griha_pravesh', en: 'Griha Pravesh', hi: 'गृह प्रवेश' }
];

const NAKSHATRAS = [
  'Ashwini', 'Bharani', 'Krittika', 'Rohini', 'Mrigashira', 'Ardra', 'Punarvasu', 'Pushya', 'Ashlesha',
  'Magha', 'Purva Phalguni', 'Uttara Phalguni', 'Hasta', 'Chitra', 'Swati', 'Vishakha', 'Anuradha',
  'Jyeshtha', 'Mula', 'Purva Ashadha', 'Uttara Ashadha', 'Shravana', 'Dhanishta', 'Shatabhisha',
  'Purva Bhadrapada', 'Uttara Bhadrapada', 'Revati'
];

const RASHIS = ['Mesha', 'Vrishabha', 'Mithuna', 'Karka', 'Simha', 'Kanya', 'Tula', 'Vrischika', 'Dhanu', 'Makara', 'Kumbha', 'Meena'];

const text = (lang: string, en: string, hi: string) => (lang === 'hi' ? hi : en);
const activityName = (lang: string, key?: string, fallback?: string) => {
  const item = ACTIVITIES.find((activity) => activity.key === key);
  if (lang === 'hi') return item?.hi || fallback || key || 'सामान्य';
  return fallback || item?.en || key || 'General';
};

const HI_LABELS: Record<string, string> = {
  Amrit: 'अमृत', Shubh: 'शुभ', Labh: 'लाभ', Chal: 'चल', Rog: 'रोग', Kaal: 'काल', Udveg: 'उद्वेग',
  Sun: 'सूर्य', Moon: 'चंद्र', Mars: 'मंगल', Mercury: 'बुध', Jupiter: 'गुरु', Venus: 'शुक्र', Saturn: 'शनि',
  Janma: 'जन्म', Sampat: 'सम्पत', Vipat: 'विपत', Kshema: 'क्षेम', Pratyari: 'प्रत्यारी', Sadhana: 'साधना', Naidhana: 'नैधन', Mitra: 'मित्र', 'Param Mitra': 'परम मित्र',
  Pratipada: 'प्रतिपदा', Dwitiya: 'द्वितीया', Tritiya: 'तृतीया', Chaturthi: 'चतुर्थी', Panchami: 'पंचमी', Shashthi: 'षष्ठी', Saptami: 'सप्तमी', Ashtami: 'अष्टमी', Navami: 'नवमी', Dashami: 'दशमी', Ekadashi: 'एकादशी', Dwadashi: 'द्वादशी', Trayodashi: 'त्रयोदशी', Chaturdashi: 'चतुर्दशी', Purnima: 'पूर्णिमा', Amavasya: 'अमावस्या',
  Ashwini: 'अश्विनी', Bharani: 'भरणी', Krittika: 'कृत्तिका', Rohini: 'रोहिणी', Mrigashira: 'मृगशीर्षा', Ardra: 'आर्द्रा', Punarvasu: 'पुनर्वसु', Pushya: 'पुष्य', Ashlesha: 'आश्लेषा', Magha: 'मघा', 'Purva Phalguni': 'पूर्वा फाल्गुनी', 'Uttara Phalguni': 'उत्तरा फाल्गुनी', Hasta: 'हस्त', Chitra: 'चित्रा', Swati: 'स्वाती', Vishakha: 'विशाखा', Anuradha: 'अनुराधा', Jyeshtha: 'ज्येष्ठा', Mula: 'मूल', 'Purva Ashadha': 'पूर्वाषाढ़ा', 'Uttara Ashadha': 'उत्तराषाढ़ा', Shravana: 'श्रवण', Dhanishta: 'धनिष्ठा', Shatabhisha: 'शतभिषा', 'Purva Bhadrapada': 'पूर्वा भाद्रपदा', 'Uttara Bhadrapada': 'उत्तरा भाद्रपदा', Revati: 'रेवती'
};

const localizeMuhuratLabel = (value: string | null | undefined, lang: 'en' | 'hi') => {
  if (lang !== 'hi' || !value) return value || '';
  if (HI_LABELS[value]) return HI_LABELS[value];
  return String(value)
    .replace(/\bShukla\b/g, 'शुक्ल')
    .replace(/\bKrishna\b/g, 'कृष्ण')
    .split(' ')
    .map((part) => HI_LABELS[part] || part)
    .join(' ');
};

const localizeMuhuratText = (value: string, lang: 'en' | 'hi') => {
  if (lang !== 'hi') return value;
  const activityLabel = activityName(lang, value.match(/for ([A-Za-z_ ]+)$/)?.[1]?.toLowerCase().replace(/\s+/g, '_'));
  return value
    .replace(/^(.+) Choghadiya is favourable$/, (_, label) => `${localizeMuhuratLabel(label, lang)} चौघड़िया अनुकूल है`)
    .replace(/^(.+) Choghadiya is not preferred$/, (_, label) => `${localizeMuhuratLabel(label, lang)} चौघड़िया इस कार्य के लिए बेहतर नहीं है`)
    .replace(/^(.+) Tithi is acceptable$/, (_, label) => `${localizeMuhuratLabel(label, lang)} तिथि स्वीकार्य है`)
    .replace(/^(.+) Tithi is generally avoided for shubh karya$/, (_, label) => `${localizeMuhuratLabel(label, lang)} तिथि शुभ कार्यों में सामान्यतः टाली जाती है`)
    .replace(/^(.+) Nakshatra is active$/, (_, label) => `${localizeMuhuratLabel(label, lang)} नक्षत्र सक्रिय है`)
    .replace(/^(.+) Hora is not ideal for .+$/, (_, label) => `${localizeMuhuratLabel(label, lang)} होरा ${activityLabel} के लिए आदर्श नहीं है`)
    .replace(/^(.+) Hora supports .+$/, (_, label) => `${localizeMuhuratLabel(label, lang)} होरा ${activityLabel} के लिए सहायक है`)
    .replace(/^Tarabala is favourable: (.+)$/, (_, label) => `ताराबल अनुकूल है: ${localizeMuhuratLabel(label, lang)}`)
    .replace(/^Tarabala is weak: (.+)$/, (_, label) => `ताराबल कमजोर है: ${localizeMuhuratLabel(label, lang)}`)
    .replace(/^Chandrabala is favourable from natal Moon$/, 'जन्म चंद्र से चंद्रबल अनुकूल है')
    .replace(/^Chandrabala is not strong from natal Moon$/, 'जन्म चंद्र से चंद्रबल मजबूत नहीं है');
};

const formatDateTime = (iso: string, lang: 'en' | 'hi' = 'en') => new Intl.DateTimeFormat(lang === 'hi' ? 'hi-IN' : 'en-IN', {
  dateStyle: 'medium',
  timeStyle: 'short',
  timeZone: 'Asia/Kolkata'
}).format(new Date(iso));

const getMuhuratShareRows = (slots: MuhuratSlot[], language: 'en' | 'hi') => (
  slots.slice(0, 5).map((slot, index) => ({
    label: `${text(language, 'Muhurat', 'मुहूर्त')} ${index + 1}`,
    value: `${formatDateTime(slot.start, language)} - ${formatDateTime(slot.end, language)} (${slot.score}/100)`
  }))
);

const Muhurat: React.FC = () => {
  const { i18n } = useTranslation();
  const { id: chartId } = useParams<{ id?: string }>();
  const language: 'en' | 'hi' = (i18n.resolvedLanguage || i18n.language || 'en').startsWith('hi') ? 'hi' : 'en';
  const [selectedCity, setSelectedCity] = React.useState<City | null>(null);
  const [chartContext, setChartContext] = React.useState<any | null>(null);
  const [activity, setActivity] = React.useState('general');
  const [generalSlots, setGeneralSlots] = React.useState<MuhuratSlot[]>([]);
  const [personalSlots, setPersonalSlots] = React.useState<MuhuratSlot[]>([]);
  const [birthNakshatraIndex, setBirthNakshatraIndex] = React.useState(1);
  const [birthRashiIndex, setBirthRashiIndex] = React.useState(1);
  const [includeNight, setIncludeNight] = React.useState(false);
  const [loading, setLoading] = React.useState<'general' | 'personal' | ''>('');
  const [error, setError] = React.useState('');
  const [showPremiumLock, setShowPremiumLock] = React.useState(false);
  const { popup, loading: trialLoading, checkFeatureAccess, closePopup } = usePremiumFeatureAccess({
    onShowPremiumLock: () => setShowPremiumLock(true)
  });
  const chartLocationReady = !chartId || Boolean(chartContext?.birthDetails?.placeOfBirth);
  const canUseLocation = Boolean(selectedCity || (chartId && chartLocationReady));
  const chartMoon = chartContext?.planets?.MOON;
  const shareLocationLabel = chartId && chartContext?.birthDetails?.placeOfBirth
    ? [
        chartContext.birthDetails.placeOfBirth.city,
        chartContext.birthDetails.placeOfBirth.state,
        chartContext.birthDetails.placeOfBirth.country
      ].filter(Boolean).join(', ') || text(language, 'Kundli birthplace', 'कुंडली जन्म स्थान')
    : selectedCity?.displayName || selectedCity?.name || 'Selected city';

  const changeLanguage = (nextLanguage: 'en' | 'hi') => {
    i18n.changeLanguage(nextLanguage);
  };

  React.useEffect(() => {
    document.title = 'Muhurat - Kundlistar Studio';
  }, []);

  React.useEffect(() => {
    if (!chartId) return;
    const loadChart = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/charts/${chartId}`);
        const chart = response.data?.data?.chart || response.data?.data;
        setChartContext(chart);
        const moon = chart?.planets?.MOON;
        if (moon?.nakshatra?.number) setBirthNakshatraIndex(Number(moon.nakshatra.number));
        if (moon?.sign?.number) setBirthRashiIndex(Number(moon.sign.number));
      } catch (_) {
        setChartContext(null);
      }
    };
    loadChart();
  }, [chartId]);

  const locationParams = () => {
    if (chartId && chartContext?.birthDetails?.placeOfBirth) {
      const place = chartContext.birthDetails.placeOfBirth;
      return {
        lat: place.latitude,
        lon: place.longitude,
        timezoneOffset: place.timezone,
        activity,
        language
      };
    }
    if (!selectedCity) throw new Error(text(language, 'Please select a city first.', 'पहले शहर चुनें।'));
    return {
      lat: selectedCity.coordinates.latitude,
      lon: selectedCity.coordinates.longitude,
      timezoneOffset: selectedCity.timezone.offset,
      activity,
      language
    };
  };

  const loadGeneral = async () => {
    try {
      setLoading('general');
      setError('');
      const response = await axios.get(`${API_BASE_URL}/muhurat/general`, {
        params: { ...locationParams(), days: 7 }
      });
      setGeneralSlots(response.data.results || []);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || text(language, 'Unable to calculate Muhurat.', 'मुहूर्त निकालने में समस्या आई।'));
    } finally {
      setLoading('');
    }
  };

  const loadPersonalized = async () => {
    try {
      setLoading('personal');
      setError('');
      const payload = {
        ...locationParams(),
        days: 30,
        ...(chartId ? { chartId } : {}),
        birthNakshatraIndex,
        birthRashiIndex,
        includeNight
      };
      await checkFeatureAccess({
        featureKey: 'PREMIUM_MUHURAT',
        apiCall: (trialAccessToken?: string) => axios.post('/muhurat/personalized', payload, {
          headers: trialAccessToken ? { 'x-premium-trial-token': trialAccessToken } : undefined
        }),
        onSuccess: (response: any) => {
          setShowPremiumLock(false);
          setPersonalSlots(response.results || []);
        }
      });
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || text(language, 'Unable to calculate personalized Muhurat.', 'व्यक्तिगत मुहूर्त निकालने में समस्या आई।'));
    } finally {
      setLoading('');
    }
  };

  const renderSlot = (slot: MuhuratSlot, index: number) => (
    <Grid key={`${slot.start}-${index}`} size={{ xs: 12, sm: 6, lg: 4 }}>
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
          <Box>
            <Typography variant="caption" color="primary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>
              {activityName(language, slot.activity, slot.activityLabel)} {text(language, 'Muhurat', 'मुहूर्त')}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {formatDateTime(slot.start, language)}
            </Typography>
          </Box>
          <Chip label={`${slot.score}/100`} color={slot.score >= 80 ? 'success' : 'primary'} size="small" />
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {formatDateTime(slot.end, language)} · {slot.durationMin} {text(language, 'min', 'मिनट')}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
          <Chip size="small" label={localizeMuhuratLabel(slot.choghadiyaLabel || slot.choghadiya, language)} />
          {slot.hora && <Chip size="small" variant="outlined" label={`${localizeMuhuratLabel(slot.horaLabel || slot.hora, language)} ${text(language, 'Hora', 'होरा')}`} />}
          {slot.tithi && <Chip size="small" variant="outlined" label={localizeMuhuratLabel(slot.tithiLabel || slot.tithi, language)} />}
          {slot.nakshatra && <Chip size="small" variant="outlined" label={localizeMuhuratLabel(slot.nakshatraLabel || slot.nakshatra, language)} />}
        </Box>
        {slot.tara && (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {text(language, 'Tarabala', 'ताराबल')}: {localizeMuhuratLabel(slot.tara.taraLabel || slot.tara.taraName, language)}
          </Typography>
        )}
        {slot.chandraBala && (
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {text(language, 'Chandrabala', 'चंद्रबल')}: {slot.chandraBala.position}
          </Typography>
        )}
        {slot.reasons.slice(0, 3).map((reason, reasonIndex) => (
          <Typography key={reasonIndex} variant="body2" color="text.secondary">• {localizeMuhuratText(reason, language)}</Typography>
        ))}
        {slot.warnings.slice(0, 2).map((warning, warningIndex) => (
          <Typography key={warningIndex} variant="body2" color="warning.main">• {localizeMuhuratText(warning, language)}</Typography>
        ))}
      </CardContent>
    </Card>
    </Grid>
  );

  return (
    <>
      <Helmet>
        <title>Muhurat Finder | Auspicious Time | Kundlistar Studio</title>
        <meta name="description" content="Find general and personalized Muhurat using Panchang, Choghadiya, Hora, Tarabala and Chandrabala." />
      </Helmet>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {text(language, 'Muhurat Finder', 'मुहूर्त फाइंडर')}
          </Typography>
          <Typography color="text.secondary">
            {text(
              language,
              'Find useful time windows using Panchang, Choghadiya and premium personal birth factors.',
              'पंचांग, चौघड़िया और प्रीमियम व्यक्तिगत जन्म कारकों से उपयोगी समय विंडो देखें।'
            )}
          </Typography>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2}>
              {!chartId ? (
                <Grid size={{ xs: 12, md: 5 }}>
                  <CitySearch
                    value={selectedCity}
                    onChange={(city) => {
                      setSelectedCity(city);
                      setError('');
                    }}
                    label={text(language, 'City', 'शहर')}
                    placeholder={text(language, 'Search city...', 'शहर खोजें...')}
                    required
                  />
                </Grid>
              ) : (
                <Grid size={{ xs: 12, md: 5 }}>
                  <Alert severity="success" sx={{ height: '100%', alignItems: 'center' }}>
                    {text(language, 'Using selected Kundli', 'चयनित कुंडली उपयोग में')}: {chartContext?.birthDetails?.name || chartId}
                  </Alert>
                </Grid>
              )}
              <Grid size={{ xs: 12, md: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>{text(language, 'Activity', 'कार्य')}</InputLabel>
                  <Select label={text(language, 'Activity', 'कार्य')} value={activity} onChange={(event) => setActivity(event.target.value)}>
                    {ACTIVITIES.map((item) => (
                      <MenuItem key={item.key} value={item.key}>{language === 'hi' ? item.hi : item.en}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <FormControl fullWidth>
                  <InputLabel>{text(language, 'Language', 'भाषा')}</InputLabel>
                  <Select label={text(language, 'Language', 'भाषा')} value={language} onChange={(event) => changeLanguage(event.target.value as 'en' | 'hi')}>
                    <MenuItem value="en">English</MenuItem>
                    <MenuItem value="hi">हिंदी</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={loading === 'general' ? <CircularProgress size={18} color="inherit" /> : <AccessTime />}
                  onClick={loadGeneral}
                  disabled={loading !== '' || !canUseLocation}
                  sx={{ minHeight: 56, fontWeight: 600 }}
                >
                  {text(language, 'Find Free', 'फ्री देखें')}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        <Grid container spacing={3}>
          <Grid size={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <LockOpen color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>{text(language, 'Free General Muhurat', 'फ्री सामान्य मुहूर्त')}</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {text(language, 'Top 3 slots for the next 7 days. Rahu Kaal, Yamaganda and Gulika are removed.', 'अगले 7 दिनों के टॉप 3 स्लॉट। राहु काल, यमगंड और गुलिक हटाए गए हैं।')}
            </Typography>
            {generalSlots.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <DailyAstrologyShareCard
                  title={text(language, "Today's Muhurat", 'आज के शुभ मुहूर्त')}
                  subtitle={text(language, 'Daily Muhurat by Kundlistar Studio', 'कुंडलिस्टार स्टूडियो दैनिक मुहूर्त')}
                  date={new Intl.DateTimeFormat(language === 'hi' ? 'hi-IN' : 'en-IN', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  }).format(new Date())}
                  location={shareLocationLabel}
                  rows={[
                    { label: text(language, 'Activity', 'कार्य'), value: activityName(language, activity) },
                    ...getMuhuratShareRows(generalSlots, language)
                  ]}
                  textMessage={[
                    text(language, "Today's Muhurat", 'आज के शुभ मुहूर्त'),
                    shareLocationLabel,
                    '',
                    `${text(language, 'Activity', 'कार्य')}: ${activityName(language, activity)}`,
                    ...getMuhuratShareRows(generalSlots, language).map((row) => `${row.label}: ${row.value}`),
                    '',
                    text(language, 'Shared via Kundlistar Studio', 'कुंडलिस्टार स्टूडियो से साझा किया गया'),
                    'Download App: https://kundlistarstudio.com?utm_source=share&utm_medium=website&utm_campaign=daily_muhurat'
                  ].join('\n')}
                  imageFileName="kundlistar-daily-muhurat.png"
                  shareTitle={text(language, 'Kundlistar Studio Muhurat', 'कुंडलिस्टार स्टूडियो मुहूर्त')}
                  imageButtonLabel={text(language, 'Share Image', 'इमेज शेयर करें')}
                  textButtonLabel={text(language, 'Share Text', 'टेक्स्ट शेयर करें')}
                  copyButtonLabel={text(language, 'Copy Text', 'टेक्स्ट कॉपी करें')}
                />
              </Box>
            )}
            <Grid container spacing={2}>
              {generalSlots.length ? generalSlots.map(renderSlot) : (
                <Grid size={12}>
                  <Alert severity="info">{text(language, chartId ? 'Click Find Free to use the selected Kundli birthplace.' : 'Select a city and click Find Free.', chartId ? 'चयनित कुंडली के जन्म स्थान से फ्री मुहूर्त देखें।' : 'शहर चुनें और फ्री देखें पर क्लिक करें।')}</Alert>
                </Grid>
              )}
            </Grid>
          </Grid>

          <Grid size={12}>
            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <WorkspacePremium color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{text(language, 'Premium Personalized Muhurat', 'प्रीमियम व्यक्तिगत मुहूर्त')}</Typography>
                </Box>
                <Grid container spacing={2}>
                  {chartId ? (
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Alert severity={chartContext ? 'info' : 'warning'}>
                        {chartContext
                          ? text(
                              language,
                              `Using birth Nakshatra/Rashi from loaded Kundli: ${chartMoon?.nakshatra?.name || NAKSHATRAS[birthNakshatraIndex - 1]} / ${chartMoon?.sign?.name || RASHIS[birthRashiIndex - 1]}`,
                              `लोडेड कुंडली से जन्म नक्षत्र/राशि उपयोग हो रही है: ${chartMoon?.nakshatra?.name || NAKSHATRAS[birthNakshatraIndex - 1]} / ${chartMoon?.sign?.name || RASHIS[birthRashiIndex - 1]}`
                            )
                          : text(language, 'Loading Kundli details...', 'कुंडली विवरण लोड हो रहे हैं...')}
                      </Alert>
                    </Grid>
                  ) : (
                    <>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                          select
                          fullWidth
                          label={text(language, 'Birth Nakshatra', 'जन्म नक्षत्र')}
                          value={birthNakshatraIndex}
                          onChange={(event) => setBirthNakshatraIndex(Number(event.target.value))}
                        >
                          {NAKSHATRAS.map((name, index) => <MenuItem key={name} value={index + 1}>{index + 1}. {name}</MenuItem>)}
                        </TextField>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <TextField
                          select
                          fullWidth
                          label={text(language, 'Birth Moon Rashi', 'जन्म चंद्र राशि')}
                          value={birthRashiIndex}
                          onChange={(event) => setBirthRashiIndex(Number(event.target.value))}
                        >
                          {RASHIS.map((name, index) => <MenuItem key={name} value={index + 1}>{index + 1}. {name}</MenuItem>)}
                        </TextField>
                      </Grid>
                    </>
                  )}
                  <Grid size={{ xs: 12, md: 3 }}>
                    <FormControlLabel
                      control={<Switch checked={includeNight} onChange={(event) => setIncludeNight(event.target.checked)} />}
                      label={text(language, 'Include night slots', 'रात के स्लॉट शामिल करें')}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, md: 3 }}>
                    <Button
                      fullWidth
                      variant="outlined"
                      startIcon={loading === 'personal' ? <CircularProgress size={18} /> : <AutoAwesome />}
                      onClick={loadPersonalized}
                      disabled={loading !== '' || trialLoading || !canUseLocation}
                      sx={{ minHeight: 56, fontWeight: 600 }}
                    >
                      {text(language, 'Find Premium', 'प्रीमियम देखें')}
                    </Button>
                  </Grid>
                </Grid>
                {showPremiumLock && (
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    {text(language, 'Your free Studio Pro access is used. Choose Studio Pro for continued access.', 'आपका निःशुल्क स्टूडियो प्रो एक्सेस उपयोग हो चुका है। आगे उपयोग के लिए स्टूडियो प्रो चुनें।')}
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>

          {personalSlots.length > 0 && (
            <Grid size={12}>
              <Grid container spacing={2}>
                {personalSlots.map(renderSlot)}
              </Grid>
            </Grid>
          )}
        </Grid>
        <PremiumTrialPopup
          open={popup.open}
          featureName={popup.featureName}
          used={popup.used}
          total={popup.total}
          remaining={popup.remaining}
          loading={trialLoading}
          onCancel={closePopup}
          onContinue={popup.nextAction}
        />
      </Container>
    </>
  );
};

export default Muhurat;
